// src/agent.ts
import { z } from "zod";
var AgentErrorCode = {
  INVALID_ARGS: "AGT_INVALID_ARGS",
  DOC_UNSUPPORTED_TYPE: "AGT_DOC_UNSUPPORTED_TYPE",
  DOC_TOO_LARGE: "AGT_DOC_TOO_LARGE",
  DOC_FETCH_FAILED: "AGT_DOC_FETCH_FAILED",
  DOC_DECRYPT_FAILED: "AGT_DOC_DECRYPT_FAILED",
  TOKEN_MINT_FAILED: "AGT_TOKEN_MINT_FAILED"
};
var AgentError = class extends Error {
  code;
  constructor(code, message, options) {
    super(message);
    this.name = code;
    this.code = code;
    if ((options == null ? void 0 : options.cause) !== void 0) {
      this.cause = options.cause;
    }
  }
};
var DEFAULT_AGENT_MODEL = "eu.anthropic.claude-opus-4-6-v1";
var AGENT_DOCUMENT_MEDIA_TYPE_BY_EXT = {
  pdf: "application/pdf",
  txt: "text/plain",
  xml: "application/xml"
};
var AGENT_SUPPORTED_DOCUMENT_EXTENSIONS = Object.keys(
  AGENT_DOCUMENT_MEDIA_TYPE_BY_EXT
);
var AGENT_MAX_DOCUMENTS_PER_CALL = 20;
var AGENT_MAX_BYTES_PER_DOC = 32 * 1024 * 1024;
var AGENT_MAX_TOTAL_BYTES = 100 * 1024 * 1024;
function extensionOf(name) {
  const idx = name.lastIndexOf(".");
  return idx === -1 ? "" : name.slice(idx + 1).toLowerCase();
}
function detectAgentDocumentMediaType(name) {
  const ext = extensionOf(name);
  const mediaType = AGENT_DOCUMENT_MEDIA_TYPE_BY_EXT[ext];
  if (!mediaType) {
    throw new AgentError(
      AgentErrorCode.DOC_UNSUPPORTED_TYPE,
      `Unsupported document type "${name}". Allowed: ${AGENT_SUPPORTED_DOCUMENT_EXTENSIONS.join(", ")}.`
    );
  }
  return mediaType;
}
var agentDocumentReferenceSchema = z.object({
  name: z.string().min(1),
  digest: z.string().min(1),
  key: z.string().min(1),
  size: z.number().int().nonnegative()
});
var agentDocumentsSchema = z.array(agentDocumentReferenceSchema).max(
  AGENT_MAX_DOCUMENTS_PER_CALL,
  `At most ${AGENT_MAX_DOCUMENTS_PER_CALL} documents per call.`
).superRefine((documents, ctx) => {
  let total = 0;
  for (const doc of documents) {
    const ext = extensionOf(doc.name);
    if (!AGENT_SUPPORTED_DOCUMENT_EXTENSIONS.includes(
      ext
    )) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unsupported document type: "${doc.name}". Allowed: ${AGENT_SUPPORTED_DOCUMENT_EXTENSIONS.join(", ")}.`
      });
    }
    if (doc.size > AGENT_MAX_BYTES_PER_DOC) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Document "${doc.name}" is ${doc.size} bytes; max ${AGENT_MAX_BYTES_PER_DOC}.`
      });
    }
    total += doc.size;
  }
  if (total > AGENT_MAX_TOTAL_BYTES) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Documents total ${total} bytes; max ${AGENT_MAX_TOTAL_BYTES} per call.`
    });
  }
});
var stratumnAgentArgsSchema = z.object({
  prompt: z.string().min(1),
  model: z.string().min(1).default(DEFAULT_AGENT_MODEL),
  allowedTools: z.object({ web: z.boolean().default(true) }),
  output: z.object({
    type: z.enum([
      "string",
      "number",
      "integer",
      "boolean",
      "object",
      "array",
      "null"
    ])
  }).passthrough().optional(),
  systemPrompt: z.string().optional(),
  maxSteps: z.number().int().positive().optional(),
  documents: agentDocumentsSchema.optional()
});
var agentWorkflowInputSchema = stratumnAgentArgsSchema.extend({
  userEmail: z.string().email()
});

// src/linkActivity.ts
import { z as z2 } from "zod";
var createLinkInputSchema = z2.object({
  traceId: z2.string(),
  actionKey: z2.string(),
  formData: z2.unknown(),
  workflowId: z2.string(),
  groupLabel: z2.string(),
  emailToImpersonate: z2.string(),
  batch: z2.object({
    isBatchAction: z2.boolean(),
    actionPositionInBatch: z2.number().int().nonnegative(),
    totalActionInBatch: z2.number().int().nonnegative()
  }).optional()
});

// src/mastercomExtended.schema.ts
import z3 from "zod";
var transactionAmountSchema = z3.object({
  displayAmount: z3.string(),
  wholeAmount: z3.string(),
  decimalAmount: z3.string(),
  numericCurrencyCode: z3.string(),
  currencyExponent: z3.string(),
  alphaCurrencyCodes: z3.array(z3.string())
});
var authorizationSchema = z3.object({
  acquirerReferenceNumber: z3.string(),
  financialNetworkCode: z3.string(),
  transactionAmount: transactionAmountSchema,
  acceptorName: z3.string(),
  cardAcceptorType: z3.string(),
  tokenizedPrimaryAccountNumber: z3.string().nullish(),
  primaryAccountNumber: z3.string(),
  processingCode: z3.string(),
  responseCode: z3.string(),
  settlementDate: z3.string(),
  switchSerialNumber: z3.string(),
  transmissionDateTime: z3.string(),
  systemsTraceAuditNumber: z3.string(),
  interchangeRateIndicator: z3.string().nullish(),
  adviceReasonCode: z3.string().nullish(),
  localCompletedAmount: transactionAmountSchema.optional()
});
var clearingSchema = z3.object({
  acquirerReferenceNumber: z3.string(),
  financialNetworkCode: z3.string(),
  transactionAmount: transactionAmountSchema,
  acceptorName: z3.string(),
  cardAcceptorType: z3.string(),
  tokenizedPrimaryAccountNumber: z3.string().nullish(),
  primaryAccountNumber: z3.string(),
  processingCode: z3.string(),
  responseCode: z3.string(),
  settlementDate: z3.string(),
  switchSerialNumber: z3.string(),
  transmissionDateTime: z3.string(),
  systemsTraceAuditNumber: z3.string(),
  interchangeRateIndicator: z3.string().nullish(),
  adviceReasonCode: z3.string().nullish(),
  localCompletedAmount: transactionAmountSchema.optional()
});
var singleMessageSchema = z3.object({
  contextId: z3.string(),
  contextType: z3.string(),
  claimId: z3.string().nullish(),
  transactionClass: z3.string().nullish(),
  type: z3.string().nullish(),
  claimAllowed: z3.string().nullish(),
  authorization: authorizationSchema,
  clearing: clearingSchema.optional()
});
var dualMessageSchema = z3.object({
  contextId: z3.string(),
  contextType: z3.string(),
  claimId: z3.string().nullish(),
  transactionClass: z3.string(),
  claimAllowed: z3.string().nullish(),
  authorization: authorizationSchema.optional(),
  clearing: clearingSchema.optional()
});
var TransactionsSearchesResponseSchema = z3.object({
  transactionsData: z3.object({
    singleMessages: z3.array(singleMessageSchema).nullable().optional(),
    dualMessages: z3.array(dualMessageSchema).nullable().optional()
  })
});
var ClaimsResponseSchema = z3.object({
  claimId: z3.string()
});
var AmountSchema = z3.object({
  displayAmount: z3.string(),
  wholeAmount: z3.string(),
  decimalAmount: z3.string(),
  alphaCurrencyCodes: z3.array(z3.string()),
  numericCurrencyCode: z3.string(),
  currencyExponent: z3.string()
});
var CompletedDocumentSchema = z3.object({
  documentCompletedId: z3.string(),
  documentCompletedFormat: z3.string(),
  documentCompletedFileName: z3.string(),
  documentCompletedFileSize: z3.string()
});
var CollaborationDetailSchema = z3.object({
  eventType: z3.string(),
  eventId: z3.string(),
  eventReferenceId: z3.string().nullish(),
  groupId: z3.string().nullish(),
  creationDateTime: z3.string(),
  eventLastModifiedBy: z3.string().nullish(),
  lastUpdatedDateTime: z3.string().nullish(),
  eventStatus: z3.string().nullish(),
  ownerUserId: z3.string().nullish(),
  collaborationAmount: AmountSchema,
  reasonCode: z3.string(),
  responseCode: z3.string().nullish(),
  memo: z3.string().nullish(),
  refundReversalType: z3.string().nullish(),
  refundReversalAmount: AmountSchema.optional(),
  refundReversalDateTime: z3.string().nullish(),
  refundReversalReferenceId: z3.string().nullish(),
  documentIndicator: z3.string().nullish(),
  documentMemo: z3.string().nullish(),
  completedDocumentsAttributes: z3.array(CompletedDocumentSchema).optional()
});
var SingleMessageDetailSchema = z3.object({
  contextId: z3.string(),
  contextType: z3.string(),
  transactionClass: z3.string().nullish(),
  type: z3.string(),
  authorization: authorizationSchema,
  clearing: clearingSchema.optional()
});
var DualMessageDetailSchema = z3.object({
  contextId: z3.string(),
  contextType: z3.string(),
  transactionClass: z3.string(),
  authorization: authorizationSchema.optional(),
  clearing: clearingSchema.optional()
});
var TransactionDetailSchema = z3.object({
  singleMessages: z3.array(SingleMessageDetailSchema).optional(),
  dualMessages: z3.array(DualMessageDetailSchema).optional()
});
var ChargebackEventSchema = z3.object({
  eventType: z3.string(),
  eventId: z3.string(),
  eventReferenceId: z3.string().nullish(),
  groupId: z3.string().nullish(),
  creationDateTime: z3.string(),
  eventLastModifiedBy: z3.string().nullish(),
  lastUpdatedDateTime: z3.string().nullish(),
  disputeAmount: AmountSchema.optional(),
  cashBackDisputeAmount: AmountSchema.optional(),
  reasonCode: z3.string().nullish(),
  clearingStatus: z3.string().nullish(),
  rejectCode: z3.string().nullish(),
  rejectReason: z3.string().nullish(),
  eventExpiryDateTime: z3.string().nullish(),
  creditPostedAsPurchase: z3.boolean().optional(),
  additionalInformation: z3.string().nullish(),
  documentIndicator: z3.string().nullish(),
  documentStatus: z3.string().nullish(),
  documentMemo: z3.string().nullish(),
  documentDueDate: z3.string().nullish(),
  completedDocumentsAttributes: z3.array(CompletedDocumentSchema).optional(),
  singleMessage: SingleMessageDetailSchema.optional()
});
var AdjustmentEventSchema = z3.object({
  eventType: z3.string(),
  eventId: z3.string(),
  eventReferenceId: z3.string().nullish(),
  groupId: z3.string().nullish(),
  creationDateTime: z3.string(),
  eventLastModifiedBy: z3.string().nullish(),
  lastUpdatedDateTime: z3.string().nullish(),
  adviceReasonCode: z3.string().nullish(),
  replacementAmount: AmountSchema.optional(),
  newPurchaseAmount: AmountSchema.optional(),
  newCashBackAmount: AmountSchema.optional(),
  reasonCode: z3.string().nullish(),
  controlNumber: z3.string().nullish(),
  clearingStatus: z3.string().nullish(),
  rejectCode: z3.string().nullish(),
  rejectReason: z3.string().nullish(),
  issuerSettlementAmount: AmountSchema.optional(),
  acquirerSettlementAmount: AmountSchema.optional(),
  documentIndicator: z3.string().nullish(),
  documentStatus: z3.string().nullish(),
  documentMemo: z3.string().nullish(),
  completedDocumentsAttributes: z3.array(CompletedDocumentSchema).optional()
});
var RepresentmentEventSchema = z3.object({
  eventType: z3.string(),
  eventId: z3.string(),
  eventReferenceId: z3.string().nullish(),
  groupId: z3.string().nullish(),
  creationDateTime: z3.string(),
  eventLastModifiedBy: z3.string().nullish(),
  lastUpdatedDateTime: z3.string().nullish(),
  disputeAmount: AmountSchema.optional(),
  cashBackDisputeAmount: AmountSchema.optional(),
  reasonCode: z3.string().nullish(),
  additionalInformation: z3.string().nullish(),
  documentIndicator: z3.string().nullish(),
  documentStatus: z3.string().nullish(),
  documentMemo: z3.string().nullish(),
  documentDueDate: z3.string().nullish(),
  completedDocumentsAttributes: z3.array(CompletedDocumentSchema).optional(),
  singleMessage: SingleMessageDetailSchema.optional()
});
var ValidActionSchema = z3.object({
  action: z3.string(),
  reasonCodes: z3.array(
    z3.object({
      reasonCode: z3.string(),
      description: z3.string(),
      documentRequiredIndicator: z3.string()
    })
  ).optional(),
  responseCodes: z3.array(
    z3.object({
      responseCode: z3.string(),
      description: z3.string(),
      documentRequiredIndicator: z3.string()
    })
  ).optional()
});
var CaseFilingSummarySchema = z3.object({
  caseId: z3.string(),
  caseStatus: z3.string(),
  caseFilingDate: z3.string().nullish(),
  caseType: z3.string().nullish()
});
var ClaimResponseSchema = z3.object({
  claimId: z3.string(),
  claimStatus: z3.string(),
  contextId: z3.string(),
  contextType: z3.string(),
  primaryAccountNumber: z3.string(),
  tokenizedPrimaryAccountNumber: z3.string().nullish(),
  acquirerReferenceNumber: z3.string(),
  issuerIca: z3.string(),
  acquirerIca: z3.string(),
  transactionAmount: AmountSchema,
  totalDisputeAmount: AmountSchema.optional(),
  ownerUserId: z3.string().nullish(),
  lastUpdatedOwnerUserId: z3.string().nullish(),
  claimCreationDateTime: z3.string(),
  claimLastUpdatedDateTime: z3.string().nullish(),
  collaborationDetails: z3.array(CollaborationDetailSchema).optional(),
  transactionDetails: TransactionDetailSchema.optional(),
  chargebackDetails: z3.array(ChargebackEventSchema).optional(),
  chargebackReversalDetails: z3.array(ChargebackEventSchema).optional(),
  adjustmentDetails: z3.array(AdjustmentEventSchema).optional(),
  adjustmentReversalDetails: z3.array(AdjustmentEventSchema).optional(),
  representmentDetails: z3.array(RepresentmentEventSchema).optional(),
  representmentReversalDetails: z3.array(RepresentmentEventSchema).optional(),
  caseFilingSummaries: z3.array(CaseFilingSummarySchema).optional(),
  validActions: z3.array(ValidActionSchema).optional(),
  sendDisputeDetails: z3.array(z3.unknown()).optional()
});
var DocumentsResponseSchema = z3.object({
  documentUploadedId: z3.string()
});
var ClaimDisputeResponseSchema = z3.object({
  eventId: z3.string(),
  eventType: z3.string()
});
var ChargebackReversalResponseSchema = z3.object({
  eventId: z3.string()
});
var QueueClaimFieldSchema = z3.object({
  fieldName: z3.string(),
  fieldValue: z3.string()
});
var QueueClaimsResponseSchema = z3.object({
  queue: z3.object({
    queueId: z3.string(),
    queueName: z3.string().optional(),
    count: z3.number().optional(),
    offset: z3.number().optional(),
    limit: z3.number().optional(),
    total: z3.number().optional(),
    sortKey: z3.string().optional(),
    sortOrder: z3.string().optional()
  }),
  data: z3.array(z3.array(QueueClaimFieldSchema))
});
var CheckDocumentDetailsResponseSchema = z3.object({
  documentAttributes: z3.array(
    z3.object({
      eventId: z3.string(),
      claimId: z3.string(),
      documentStatus: z3.string(),
      completedDocumentsAttributes: z3.array(
        z3.object({
          documentCompletedId: z3.string(),
          documentCompletedFormat: z3.string(),
          documentCompletedFileName: z3.string(),
          documentCompletedFileSize: z3.string()
        })
      )
    })
  )
});
var RetrieveProcessedDocumentsResponseSchema = z3.object({
  fileName: z3.string(),
  file: z3.string()
});
var MastercomResponseSchema = z3.union([
  TransactionsSearchesResponseSchema,
  ClaimsResponseSchema,
  ClaimResponseSchema,
  DocumentsResponseSchema,
  ClaimDisputeResponseSchema,
  ChargebackReversalResponseSchema,
  QueueClaimsResponseSchema,
  CheckDocumentDetailsResponseSchema,
  RetrieveProcessedDocumentsResponseSchema
]);
var HttpMethod = z3.enum(["GET", "HEAD", "POST", "PUT", "DELETE"]);
var CoercedHttpMethod = z3.string().transform((val) => val.toUpperCase()).pipe(HttpMethod).default("POST");
var MastercomBaseArgsSchema = z3.object({
  endpointRoute: z3.string().min(1, "endpointRoute is required"),
  body: z3.object({}).catchall(z3.any()).optional(),
  queryParams: z3.object({}).catchall(z3.unknown()).optional(),
  endpointMethod: CoercedHttpMethod
});
var callMastercomEndPointArgsSchema = MastercomBaseArgsSchema.extend({
  authMastercardSigner: z3.string().min(1, "authMastercardSigner is required"),
  bodyString: z3.string(),
  actualCallUrl: z3.string()
}).strict();

// src/media-api.ts
import { z as z4 } from "zod";
var PdfFieldType = /* @__PURE__ */ ((PdfFieldType2) => {
  PdfFieldType2["Text"] = "text";
  PdfFieldType2["Checkbox"] = "checkbox";
  PdfFieldType2["RadioGroup"] = "radioGroup";
  PdfFieldType2["Dropdown"] = "dropdown";
  return PdfFieldType2;
})(PdfFieldType || {});
var MediaApiFileResponseSchema = z4.object({
  name: z4.string(),
  digest: z4.string(),
  bytes: z4.number().optional(),
  size: z4.number(),
  created_at: z4.string(),
  key: z4.string()
});
var MediaApiResponseArraySchema = z4.array(MediaApiFileResponseSchema);
var PdfFieldDataSchema = z4.object({
  type: z4.enum([
    "checkbox" /* Checkbox */,
    "text" /* Text */,
    "radioGroup" /* RadioGroup */,
    "dropdown" /* Dropdown */
  ]),
  name: z4.string().min(1, "Field name cannot be empty"),
  value: z4.string()
});
var AvailablePdfFieldsSchema = z4.object({
  type: z4.enum([
    "checkbox" /* Checkbox */,
    "text" /* Text */,
    "radioGroup" /* RadioGroup */,
    "dropdown" /* Dropdown */
  ]),
  fieldName: z4.string().min(1, "Field name cannot be empty"),
  mandatory: z4.boolean()
});
var TemplateFileInfoSchema = z4.object({
  name: z4.string(),
  digest: z4.string(),
  bytes: z4.number().optional(),
  key: z4.string(),
  created_at: z4.string().optional(),
  download_url: z4.string().url().optional()
});
var EncryptedFileSchema = z4.object({
  name: z4.string(),
  digest: z4.string(),
  size: z4.number(),
  bytes: z4.number().optional(),
  created_at: z4.string().optional(),
  key: z4.string().optional()
});
var generatePdfSchema = z4.object({
  generatedFileName: z4.string(),
  traceId: z4.string(),
  templateKey: z4.string()
}).and(
  z4.discriminatedUnion("version", [
    z4.object({
      version: z4.literal("pdf-lib"),
      fillData: z4.array(PdfFieldDataSchema)
    }),
    z4.object({
      version: z4.literal("docx-templater"),
      fillData: z4.record(z4.string(), z4.unknown()),
      convertToPdf: z4.boolean().optional(),
      preprocessSlate: z4.boolean().optional()
    })
  ])
);
var fileRecordSchema = z4.object({
  name: z4.string(),
  digest: z4.string(),
  size: z4.number(),
  created_at: z4.string(),
  key: z4.string()
});
var templateSchema = z4.object({
  file: z4.string(),
  templateFileInfo: fileRecordSchema,
  availableFields: z4.array(
    z4.object({
      type: z4.string(),
      fieldName: z4.string(),
      mandatory: z4.boolean()
    })
  )
});
var workflowConfigSchema = z4.object({
  pdf_template: z4.record(z4.string(), templateSchema)
});
var baseGeneratePdfSchema = z4.object({
  templateFileInfo: EncryptedFileSchema,
  availableFields: z4.array(AvailablePdfFieldsSchema),
  generatedFileName: z4.string()
});
var generatePdfBodySchema = z4.discriminatedUnion("version", [
  baseGeneratePdfSchema.extend({
    version: z4.literal("pdf-lib"),
    fillData: z4.array(PdfFieldDataSchema)
  }),
  baseGeneratePdfSchema.extend({
    version: z4.literal("docx-templater"),
    fillData: z4.record(z4.string(), z4.unknown()),
    convertToPdf: z4.boolean().optional(),
    preprocessSlate: z4.boolean().optional()
  })
]);

// src/visitor.schema.ts
import { z as z5 } from "zod";
var visitorErrorCodeSchema = z5.enum([
  "VISITOR_LINK_INVALID",
  "VISITOR_LINK_EXPIRED",
  "VISITOR_LINK_USED",
  "VISITOR_LINK_NO_LONGER_NEEDED",
  "VISITOR_FORM_INVALID",
  "VISITOR_FILE_REJECTED",
  "VISITOR_FILE_NOT_FOUND",
  "VISITOR_RATE_LIMITED",
  "VISITOR_CODE_REQUIRED",
  "VISITOR_CODE_INVALID",
  "VISITOR_CODE_EXPIRED",
  "VISITOR_CODE_LOCKED"
]);
var visitorCodeSentSchema = z5.object({
  expiresAt: z5.string(),
  resendAvailableAt: z5.string()
});
var visitorGateSchema = z5.object({
  email: z5.string(),
  code: visitorCodeSentSchema.nullable(),
  codesLeft: z5.number()
});
var visitorErrorBodySchema = z5.object({
  code: visitorErrorCodeSchema,
  message: z5.string(),
  gate: visitorGateSchema.optional()
});
var visitorGroupSchema = z5.object({
  rowId: z5.string(),
  label: z5.string(),
  name: z5.string(),
  avatar: z5.string().nullable()
});
var visitorActionPayloadSchema = z5.object({
  grant: z5.object({
    email: z5.string(),
    expiresAt: z5.string(),
    requireOtp: z5.boolean()
  }),
  workflow: z5.object({
    rowId: z5.string(),
    name: z5.string(),
    groups: z5.array(visitorGroupSchema),
    config: z5.object({
      rowId: z5.string(),
      info: z5.unknown().nullable(),
      definitions: z5.unknown().nullable()
    })
  }),
  authorGroup: visitorGroupSchema,
  action: z5.object({
    key: z5.string(),
    title: z5.string(),
    description: z5.string().nullable(),
    icon: z5.string().nullable(),
    form: z5.object({
      schema: z5.record(z5.string(), z5.unknown()),
      uiSchema: z5.record(z5.string(), z5.unknown())
    }).nullable()
  }),
  trace: z5.object({
    rowId: z5.string(),
    name: z5.string().nullable(),
    headLinkHash: z5.string(),
    state: z5.object({
      data: z5.record(z5.string(), z5.unknown()),
      meta: z5.record(z5.string(), z5.unknown()).nullable(),
      nextActions: z5.unknown().nullable()
    })
  })
});
var visitorSubmitBodySchema = z5.object({
  formData: z5.record(z5.string(), z5.unknown())
});
var visitorVerifyBodySchema = z5.object({
  code: z5.string().regex(/^\d{6}$/)
});
var visitorVerifiedSchema = z5.object({
  verifiedAt: z5.string(),
  session: z5.string()
});
var visitorLinkMetadataSchema = z5.object({
  visitor: z5.object({
    grantId: z5.string(),
    email: z5.string(),
    verification: z5.object({ method: z5.literal("email-otp"), verifiedAt: z5.string() }).optional()
  })
});
var visitorSubmitResultSchema = z5.object({ linkHash: z5.string() });
export {
  AGENT_DOCUMENT_MEDIA_TYPE_BY_EXT,
  AGENT_MAX_BYTES_PER_DOC,
  AGENT_MAX_DOCUMENTS_PER_CALL,
  AGENT_MAX_TOTAL_BYTES,
  AGENT_SUPPORTED_DOCUMENT_EXTENSIONS,
  AdjustmentEventSchema,
  AgentError,
  AgentErrorCode,
  AvailablePdfFieldsSchema,
  CaseFilingSummarySchema,
  ChargebackEventSchema,
  ChargebackReversalResponseSchema,
  CheckDocumentDetailsResponseSchema,
  ClaimDisputeResponseSchema,
  ClaimResponseSchema,
  ClaimsResponseSchema,
  CoercedHttpMethod,
  DEFAULT_AGENT_MODEL,
  DocumentsResponseSchema,
  DualMessageDetailSchema,
  EncryptedFileSchema,
  HttpMethod,
  MastercomBaseArgsSchema,
  MastercomResponseSchema,
  MediaApiResponseArraySchema,
  PdfFieldDataSchema,
  PdfFieldType,
  QueueClaimsResponseSchema,
  RepresentmentEventSchema,
  RetrieveProcessedDocumentsResponseSchema,
  SingleMessageDetailSchema,
  TemplateFileInfoSchema,
  TransactionDetailSchema,
  TransactionsSearchesResponseSchema,
  ValidActionSchema,
  agentDocumentReferenceSchema,
  agentDocumentsSchema,
  agentWorkflowInputSchema,
  authorizationSchema,
  callMastercomEndPointArgsSchema,
  clearingSchema,
  createLinkInputSchema,
  detectAgentDocumentMediaType,
  dualMessageSchema,
  extensionOf,
  generatePdfBodySchema,
  generatePdfSchema,
  singleMessageSchema,
  stratumnAgentArgsSchema,
  templateSchema,
  transactionAmountSchema,
  visitorActionPayloadSchema,
  visitorCodeSentSchema,
  visitorErrorBodySchema,
  visitorErrorCodeSchema,
  visitorGateSchema,
  visitorGroupSchema,
  visitorLinkMetadataSchema,
  visitorSubmitBodySchema,
  visitorSubmitResultSchema,
  visitorVerifiedSchema,
  visitorVerifyBodySchema,
  workflowConfigSchema
};
//# sourceMappingURL=index.mjs.map